import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMeetingNotes } from '../../api/healthCoach/useMeetingNotes';
import { AddIcon, Box, Button, Flex, Heading, Text, VStack } from 'native-base';
import { Accordions } from '../Form/Accordions';
import { MeetingContent } from './MeetingContent';
import { Loader } from '../Loader';
import { MeetingHeader } from './MeetingHeader';
import { useUpdateMeetingNotes } from '../../api/healthCoach/mutations/useUpdateMeetingNotes';
import { buildDefaultMeeting } from './utils';

export const MeetingNotes = () => {
  const { patient } = useParams();
  const { meetingNotes, isLoading, isError } = useMeetingNotes(patient ?? '');
  const { mutation } = useUpdateMeetingNotes();
  const [editingMap, setIsEditingMap] = useState({});
  const [meetingItems, setMeetingItems] = useState(meetingNotes ?? []);
  const canAddMeeting = meetingItems?.every((meeting) => !!meeting.id);

  useEffect(() => {
    if (!meetingNotes) return;

    setMeetingItems(meetingNotes);
  }, [meetingNotes]);

  const addNewMeeting = () => {
    // make room for the meeting is going to be inserted on top
    // preserving the existing values
    const newEditingMap = meetingItems.reduce((map, meetingItem, i) => {
      return { ...map, [i + 1]: editingMap[i] };
    }, {});
    setMeetingItems([buildDefaultMeeting(patient), ...meetingItems]);
    setIsEditingMap({ ...newEditingMap, 0: true });
  };

  const saveChanges = async (meeting, index) => {
    await mutation.mutateAsync({
      method: !!meeting.id ? 'PATCH' : 'POST',
      meeting,
    });

    const newEditingMap = {
      ...editingMap,
      [index]: !editingMap[index],
    };
    setIsEditingMap(newEditingMap);
  };

  if (isLoading) {
    return (
      <Box backgroundColor="muted.100" p={0}>
        <Loader loading={true} />
      </Box>
    );
  }

  return (
    <VStack>
      <Flex flexDir={'row'} alignItems={'center'}>
        <Heading textAlign="left" pr={4}>
          Meeting Notes
        </Heading>

        {canAddMeeting && (
          <Button
            onPress={addNewMeeting}
            leftIcon={
              <AddIcon name="cog-outline" as="Ionicons" color="black" />
            }
            size="xs"
            type="button"
            variant={'outline'}
            w={'80px'}
            minH={'32px'}
            h={'32px'}
            rounded={3}
            _hover={{
              bg: 'muted.200',
            }}
            _focus={{
              bg: 'transparent',
            }}
            _pressed={{
              bg: 'transparent',
            }}
          >
            <Text color={'black'} fontSize={'xs'} fontWeight={'semibold'}>
              ADD
            </Text>
          </Button>
        )}
      </Flex>

      {(isError || mutation.isError) && (
        <Text mb={6} fontSize={'sm'} color={'error.600'}>
          An error occurred while trying to load or save the meeting notes:
          please refresh the page and try again.
        </Text>
      )}

      {!meetingItems.length && (
        <Text fontSize={'sm'} py={3}>
          No Meeting Notes
        </Text>
      )}

      {!!meetingItems.length && (
        <Accordions
          content={(item, index) => (
            <MeetingContent
              meeting={item}
              index={index}
              isEditing={!!editingMap[index]}
              isLoading={mutation.isLoading}
              key={item.id ?? 'new'}
              onSave={(updatedMeeting) => {
                const updatedMeetingItems = [
                  ...meetingItems.slice(0, index),
                  updatedMeeting,
                  ...meetingItems.slice(index + 1),
                ];
                setMeetingItems(updatedMeetingItems);
                saveChanges(updatedMeeting, index);
              }}
            />
          )}
          header={(item, index, isExpanded) => (
            <MeetingHeader
              isExpanded={isExpanded}
              item={item}
              onEdit={() => {
                const newEditingMap = {
                  ...editingMap,
                  [index]: !editingMap[index],
                };
                setIsEditingMap(newEditingMap);
              }}
              onRemove={() => {
                const updatedMeetingItems = [
                  ...meetingItems.slice(0, index),
                  ...meetingItems.slice(index + 1),
                ];
                setMeetingItems(updatedMeetingItems);
              }}
            />
          )}
          items={meetingItems}
        />
      )}
    </VStack>
  );
};
